import {
  scrollToElement,
  removeUnitFromValue,
} from "../../../../js/utils/helper/helper";
import { EVENTS } from "../../../../js/constants";

class Filters {
  /**
   * @param {HTMLElement} element - the filters element
   */
  constructor(element) {
    this.element = element;

    this.config = {
      selectors: {
        innerContentSelector: ".js-Accordion-innerContent",
        extendableAreaSelector: ".js-Filters-extendableArea",
        toggleTemplateSelector: ".js-Filters-toggleTemplate",
        toggleSelector: ".js-Filters-toggle",
      },
      customProperties: {
        transition: "--default-transition-time",
      },
    };

    this._handleMouseEvents = this._handleMouseEvents.bind(this);
    this._init();
  }

  /**
   * init
   *
   * @private
   */
  _init() {
    window.addEventListener(
      EVENTS.VIEWPORT_CHANGED,
      ({ detail }) => {
        if (detail.viewport === "M" || detail.viewport === "S") {
          this._addToggleButton();
          this._toggleFocusOnInputs({ enableFocus: false });
          this._bind();
        }
      },
      { once: true }
    );
  }

  /**
   * adds the toggle button to the page only if js is enabled.
   * There is no sense to have button on the page if js is disabled
   *
   * @private
   */
  _addToggleButton() {
    const template = this.element.querySelector(
      this.config.selectors.toggleTemplateSelector
    );
    const content = template.content.firstElementChild.cloneNode(true);
    this.element.replaceChild(content, template);
  }

  /**
   * binds events
   *
   * @private
   */
  _bind() {
    this.element
      .querySelector(this.config.selectors.toggleSelector)
      .addEventListener("click", this._handleMouseEvents);
  }

  /**
   * handle mouse clicks
   *
   * @private
   */
  _handleMouseEvents() {
    const toggleElement = this.element.querySelector(
      this.config.selectors.toggleSelector
    );

    if (toggleElement.getAttribute("aria-expanded") === "true") {
      this._closeContent(toggleElement);
    } else {
      this._openContent(toggleElement);
      this._scrollToElement(
        this.element.querySelector(this.config.selectors.extendableAreaSelector)
      );
    }
  }

  /**
   * scrolls to element
   *
   * @param {HTMLElement} element - element which should be scrolled to
   * @private
   */
  _scrollToElement(element) {
    const transition = getComputedStyle(
      document.documentElement
    ).getPropertyValue(this.config.customProperties.transition);

    const timeout = removeUnitFromValue(transition);
    scrollToElement(element, "smooth", timeout);
  }

  /**
   * close content
   *
   * @param {HTMLElement} element - element which should be closed
   * @private
   */
  _closeContent(element) {
    element.setAttribute("aria-expanded", "false");
    element.blur(); // to remove focus styles
    this._toggleFocusOnInputs({ enableFocus: false });
  }

  /**
   * open content
   *
   * @param {HTMLElement} element - element which should be open
   * @private
   */
  _openContent(element) {
    element.setAttribute("aria-expanded", "true");
    this._toggleFocusOnInputs({ enableFocus: true });
  }

  /**
   * enable/disable focus on inputs
   * to prevent inputs get focused in the hidden elements (with `Tab` keyboard key)
   * by toggling tabindex=-1 on all inputs inside the sibling of the given element
   *
   * @param {object} state
   * @param {boolean} state.enableFocus - state
   * @private
   */
  _toggleFocusOnInputs({ enableFocus }) {
    const inputs = this.element
      .querySelector(this.config.selectors.extendableAreaSelector)
      .querySelectorAll("input, select, button");

    inputs.forEach((input) => {
      if (enableFocus) {
        input.removeAttribute("tabindex");
      } else {
        input.setAttribute("tabindex", "-1");
      }
    });
  }
}

export default Filters;
